import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import { AddGuardianModalComponent } from '@se-po/se-persona-picker-feature-add-guardian-modal'
import {
  HouseholdPermissionService,
  HouseholdPersonasService,
  HouseholdProfilesService,
  HouseholdRelatedGuardiansService
} from '@se-po/shared-data-access-services'
import { environment } from '@se-po/shared-environments'
import { EditPersonaModalComponent } from '@se-po/shared-feature-edit-persona-modal'
import { map, Observable, of } from 'rxjs'
import { SeFeThemesLoader } from 'se-fe-themes'
import {
  CentralServiceResources as cs,
} from 'se-resource-types'
import { RelatedGuardiansResult } from 'se-resource-types/dist/lib/CentralService/Households'
import { AddProfileModalComponent } from '../add-profile-modal/add-profile-modal.component'

@Component({
  selector: 'se-po-persona-picker-element',
  templateUrl: './persona-picker.component.html',
  styleUrls: ['./persona-picker.component.scss'],
  encapsulation: ViewEncapsulation.ShadowDom
})
export class PersonaPickerComponent implements OnInit {
  @Input() public header?: string
  @Input() public profilesframes: string
  @Input() public set excludeManagedProfiles(value: string | boolean) {
    this._excludeManagedProfiles = value === 'true' || value === true
  }
  public get excludeManagedProfiles(): boolean {
    return this._excludeManagedProfiles
  }
  @Input() public set excludeSelfProfile(value: string | boolean) {
    this._excludeSelfProfile = value === 'true' || value === true
  }
  public get excludeSelfProfile(): boolean {
    return this._excludeSelfProfile
  }

  @Output() public personaPicked = new EventEmitter<cs.Households.UserProfile>()
  @Output() public action = new EventEmitter<{ profile: cs.Households.UserProfile; action: { name: string } }>()

  @ViewChild('addGuardianModal') public addGuardianModal: AddGuardianModalComponent
  @ViewChild('addProfileModal') public addProfileModal: AddProfileModalComponent
  @ViewChild('editPersonaModal') public editPersonaModal: EditPersonaModalComponent

  public guardianInvites: cs.Households.PersonaGuardianInvite[] = []
  public profiles: cs.Households.UserProfile[]
  public selfProfile: cs.Households.UserProfile
  public selfPersona: cs.Profiles.Persona
  public permissions: HouseholdPermissionService
  public managedProfiles: cs.Households.UserProfile[]
  public userProfilesFrames: cs.Households.UserProfileFrame[]
  public editInfoProfile: cs.Households.UserProfile
  public themeLoaded = false
  public userId: number
  public addPersona: cs.Profiles.Persona
  public loading = false

  private _excludeManagedProfiles = false // default false
  private _excludeSelfProfile = false // default false
  private guardiansReady = false
  private relatedGuardians: RelatedGuardiansResult[]
  private profilesReady = false
  private personaGuardianEmails: string[] = []

  constructor(
    private el: ElementRef,
    private householdPersonasService: HouseholdPersonasService,
    private householdProfilesService: HouseholdProfilesService,
    private householdRelatedGuardiansService: HouseholdRelatedGuardiansService,
    private translateService: TranslateService
  ) {
    this.loadTheme()
  }

  public get ready(): boolean {
    return this.guardiansReady && this.profilesReady
  }

  public ngOnInit(): void {
    this.translateService.setDefaultLang('en')
    this.loadProfiles()
    this.loadGuardians()
    this.parseProfilesFrames()
  }

  public parseProfilesFrames(): void {
    this.userProfilesFrames = []
    if (this.profilesframes) {
      this.userProfilesFrames = JSON.parse(this.profilesframes)
    }
  }

  public onEdit(p: cs.Households.UserProfile, guardianEmails: string[] = null): void {
    this.personaGuardianEmails = guardianEmails
    this.editInfoProfile = p
    this.editPersonaModal.open(this.editInfoProfile)
  }

  public onEditMissingInfo(p: cs.Households.UserProfile): void {
    this.getPersonaGuardianEmails(p).subscribe(guardianEmails => {
      this.onEdit(p, guardianEmails)
    })
  }

  public onPick(p: cs.Households.UserProfile): void {
    this.editInfoProfile = undefined

    this.getPersonaGuardianEmails(p).subscribe(guardianEmails => {
      if (this.canSuggestGuardians(guardianEmails)) {
        this.addGuardianModal.open(p.persona, guardianEmails)
      }
      else {
        this.personaPicked.emit(p)
      }
    })
  }

  public onAction(e: { profile: cs.Households.UserProfile; action: any }): void {
    this.action.emit(e)
  }

  public onAddGuardianClose(persona: cs.Profiles.Persona): void {
    if (this.editInfoProfile) return

    const pickedProfile = this.profiles.find(profile => profile.persona.id === persona.id)
    this.personaPicked.emit(pickedProfile)
  }

  public onAddProfileClick(evt): void {
    this.addPersona = null
    this.addProfileModal.open(evt)
  }


  public onAddProfileClose(): void {
    if (!this.addPersona) return
    this.personaPicked.emit(this.profiles.find(p => p.persona.id === this.addPersona.id))
  }

  public onPersonaSave(): void {
    this.profilesReady = false
    this.loadProfiles()
  }

  public onAddProfile(persona: cs.Profiles.Persona): void {
    this.addPersona = persona
    this.loadProfiles()
  }

  private loadProfiles(): void {
    this.householdProfilesService.mine()
      .subscribe(
        (result: cs.Households.UserProfilesResult) => {
          if (!result) return

          this.permissions = HouseholdPermissionService.forAccount(result)
          this.profiles = this.permissions.profiles
          this.selfProfile = this.permissions.selfProfile
          this.selfPersona = this.selfProfile.persona
          this.managedProfiles = this.loadManagedProfiles(this.permissions.managedProfiles)
          this.userId = this.permissions.user.id
          this.profilesReady = true
          if (this.editInfoProfile && this.canSuggestGuardians(this.personaGuardianEmails)) {
            const profile = this.profiles.find(p => p.persona.id === this.editInfoProfile.persona.id)
            this.addGuardianModal.open(profile.persona, this.personaGuardianEmails)
          }
        }
      )
  }

  private loadManagedProfiles(profiles: cs.Households.UserProfile[]): cs.Households.UserProfile[] {
    if (this.excludeSelfProfile) {
      profiles = profiles.filter(
        item => item.relationship !== 'self'
      )
    }
    if (this.excludeManagedProfiles) {
      profiles = profiles.filter(
        item => item.relationship !== 'other'
      )
    }
    return profiles
  }

  private loadGuardians(): void {
    this.householdRelatedGuardiansService.mine().subscribe(
      (result: cs.Households.RelatedGuardiansResult[]) => {
        this.relatedGuardians = result
        this.guardiansReady = true
      }
    )
  }

  private getPersonaGuardianEmails(profile: cs.Households.UserProfile): Observable<string[]> {
    // only prompt account owners of child subprofiles to add guardians
    if (profile.access !== 'owner' || profile.relationship === 'self') return of(null)

    return this.householdPersonasService.findPersonaGuardianInvites(profile.persona.id)
      .pipe(
        map(invites => {
          const guardians = this.relatedGuardians.filter(relatedGuardian =>
            relatedGuardian.profiles.find(p => p.persona.id === profile.persona.id && p.access !== 'owner'))
          return [
            this.permissions.user.email_address.address,
            ...invites.map(invite => invite.email_address),
            ...guardians.map(guardian => guardian.user.email_address.address)
          ]
        })
      )
  }

  private canSuggestGuardians(guardianEmails: string[]): boolean {
    // can suggest a guardian if there are 1 or less guardians, plus the account owner.
    return guardianEmails && guardianEmails?.length < 3
  }

  private async loadTheme(): Promise<void> {
    await SeFeThemesLoader.load({
      supportedThemes: 'light|dark',
      shadowRoot: this.el.nativeElement.shadowRoot,
      url: environment.seFeAngularServices
    })
    this.themeLoaded = true
  }
}
